import { Divider, MenuItem, Stack, Typography } from '@mui/material';
import {
  type BackupPolicyType,
  ScheduleMode,
  type BackupSchedule,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { TimeSelectorPopover } from '@/components/date/timeSelectorPopover';
import { OptionMenu } from '@/components/option-menu/option-menu';
import type {
  OptionMenuItemType,
  OptionType,
} from '@/components/option-menu/option-menu';
import {
  Schedule,
  ScheduleContainer,
  ScheduleSelect,
} from '@/components/schedule/schedule';
import { BackupVaultSelectionDialog } from '@/components/vaults/backupVaultSelectionDialog';
import { VaultTag } from '@/components/vaults/vaultTag';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { dayjs } from '@/utils/dayjs';

import { DayOfMonthSelector } from '../controls/dayOfMonthSelector';
import { DayOfWeekSelector } from '../controls/dayOfWeekSelector';
import { DaysOfYearSelector } from '../controls/daysOfYearSelector';
import { FrequencyWindowSelector } from '../controls/frequencyWindowSelector';
import { HoursSelector } from '../controls/hoursSelector';
import { RetentionPopover } from '../controls/retentionSelectionPopover';
import {
  annuallyFrequency,
  collectDaysAndMonths,
  dailyFrequency,
  deconstructCron,
  getFrequencyByWindow,
  getHoursDisplay,
  getWindowDurationDisplay,
  getWindowHoursDuration,
  lessThanDayOptions,
  monthlyFrequency,
  weeklyFrequency,
  type Frequency,
} from '../frequency';

interface BackupPolicyScheduleProps {
  schedule: BackupSchedule;
  deleteAllowed?: boolean;
  onUpdate: (schedule: BackupSchedule) => void;
  onDuplicate: (schedule: BackupSchedule) => void;
  onDelete: (schedule: BackupSchedule) => void;
  mode: ScheduleMode;
  policyType: BackupPolicyType;
}

export function BackupPolicySchedule(props: BackupPolicyScheduleProps) {
  const { vaults } = useBackupVaults();
  const [dialogOpen, setDialogOpen] = useState(false);

  const onUpdatedRetention = (retentionInDays: number) => {
    props.onUpdate({
      ...props.schedule,
      backupRetention: retentionInDays,
    });
  };

  const frequency = getFrequencyByWindow(props.schedule.windows);

  const minimumRetention = frequency?.minimumRetentionInDays;

  const vault = vaults.find((v) => v.id === props.schedule.vaultId);

  return (
    <ScheduleContainer>
      <Schedule direction='row' alignItems='center' gap='16px'>
        <Stack gap='12px'>
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>Back up</Typography>
            <FrequencyWindowSelector
              schedule={props.schedule}
              onUpdate={props.onUpdate}
              policyType={props.policyType}
            />
            <Typography>and retain for</Typography>
            <RetentionPopover
              onUpdate={onUpdatedRetention}
              initialRetentionInDays={props.schedule.backupRetention}
              minimumRetentionInDays={minimumRetention}
            />
            <Typography>on</Typography>
            <ScheduleSelect
              sx={{
                '& .MuiSelect-select:focus': {
                  backgroundColor: 'transparent',
                },
                '& .MuiTypography-root': {
                  color: 'var(--mui-palette-text-disabled)',
                },
              }}
              data-testid='create-backup-policy-vault-select'
              variant='standard'
              value={props.schedule.vaultId || 0}
              open={false}
              native={false}
              onClick={() => {
                setDialogOpen(true);
              }}
            >
              {(!props.schedule.vaultId || !vault) && (
                <MenuItem value={props.schedule.vaultId || 0}>
                  <Typography className='font-medium'>Choose vault</Typography>
                </MenuItem>
              )}

              {props.schedule.vaultId && vault && (
                <MenuItem value={props.schedule.vaultId}>
                  <VaultTag
                    vault={vault}
                    sx={{ margin: 0, marginRight: '16px', cursor: 'pointer' }}
                  />
                </MenuItem>
              )}
            </ScheduleSelect>
          </Stack>
          {props.mode === ScheduleMode.SCHEDULE_MODE_ADVANCED && (
            <AdvancedSchedule
              schedule={props.schedule}
              onUpdate={props.onUpdate}
            />
          )}
        </Stack>
        <OptionMenu
          options={[
            {
              id: 'duplicate-backup-policy',
              text: 'Duplicate',
            },
            {
              id: 'delete-backup-policy',
              text: 'Delete',
              menuItemProps: { disabled: !props.deleteAllowed },
            },
          ]}
          onOptionSelected={(option: OptionType): void => {
            switch ((option as OptionMenuItemType).text) {
              case 'Duplicate':
                props.onDuplicate(props.schedule);
                break;
              case 'Delete':
                props.onDelete(props.schedule);
                break;
            }
          }}
        />
      </Schedule>
      <BackupVaultSelectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(vault) => {
          props.onUpdate({ ...props.schedule, vaultId: vault.id });
          setDialogOpen(false);
        }}
        scheduleWindow={
          getFrequencyByWindow(props.schedule.windows)?.text?.toLowerCase() ||
          ''
        }
        selectedVaults={props.schedule.vaultId ? [props.schedule.vaultId] : []}
        isLoading={false}
      />
    </ScheduleContainer>
  );
}

const AdvancedSchedule = ({
  schedule,
  onUpdate,
}: {
  schedule: BackupSchedule;
  onUpdate: (schedule: BackupSchedule) => void;
}) => {
  const frequency = getFrequencyByWindow(schedule.windows);
  if (!frequency) {
    return null;
  }

  const {
    minutes: minutesStr,
    hours: hoursStr,
    dayOfMonth,
    month,
    dayOfWeek,
  } = deconstructCron(schedule.windows);

  const minutes = parseInt(minutesStr);
  const hours = parseInt(hoursStr);

  return (
    <>
      <Divider sx={{ borderStyle: 'dashed' }} />
      <Stack direction='row' alignItems='center' gap='8px'>
        {frequency === weeklyFrequency && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>Schedule for</Typography>
            <DayOfWeekSelector
              days={dayOfWeek.split(',').map((x) => parseInt(x))}
              onChange={(days) =>
                onUpdate({
                  ...schedule,
                  windows: [
                    minutesStr,
                    hoursStr,
                    dayOfMonth,
                    month,
                    days.join(','),
                  ].join(' '),
                })
              }
            />
          </Stack>
        )}
        {frequency === monthlyFrequency && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>Schedule for the</Typography>
            <DayOfMonthSelector
              days={dayOfMonth.split(',')}
              onChange={(days) =>
                onUpdate({
                  ...schedule,
                  windows: [
                    minutesStr,
                    hoursStr,
                    days.join(','),
                    month,
                    dayOfWeek,
                  ].join(' '),
                })
              }
            />
          </Stack>
        )}
        {frequency === annuallyFrequency && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>Schedule for</Typography>
            <DaysOfYearSelector
              title='Select at least one backup date for each year.'
              days={collectDaysAndMonths(schedule.windows).map((x) => ({
                ...x,
                year: dayjs.utc().year(),
              }))}
              onChange={(days) =>
                onUpdate({
                  ...schedule,
                  windows: [
                    minutesStr,
                    hoursStr,
                    days.map((x) => x.day).join(','),
                    days.map((x) => x.month).join(','),
                    dayOfWeek,
                  ].join(' '),
                })
              }
            />
          </Stack>
        )}
        {!lessThanDayOptions.some((x) => x === frequency) && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography>
              {frequency === dailyFrequency ? 'Schedule for' : 'at'}
            </Typography>
            <TimeSelectorPopover
              hours={isNaN(hours) ? 0 : hours}
              minutes={isNaN(minutes) ? 0 : minutes}
              onChange={(hours, minutes) =>
                onUpdate({
                  ...schedule,
                  windows: [minutes, hours, dayOfMonth, month, dayOfWeek].join(
                    ' '
                  ),
                })
              }
            />
            <Typography>and start within</Typography>
            <HoursSelector
              hours={schedule.duration / 60}
              onChange={(hours) =>
                onUpdate({
                  ...schedule,
                  duration: hours * 60,
                })
              }
            />
          </Stack>
        )}
        {lessThanDayOptions.some((x) => x === frequency) && (
          <AdvancedHighFrequency frequency={frequency} />
        )}
      </Stack>
    </>
  );
};

const AdvancedHighFrequency = ({ frequency }: { frequency: Frequency }) => {
  const { hours, minutes } = deconstructCron(frequency.windows);
  const durationInHours = getWindowHoursDuration(frequency);
  const durationDisplay = getWindowDurationDisplay(durationInHours);

  return (
    <Stack direction='row' alignItems='center' gap='12px'>
      <Typography>{`Scheduled for ${getHoursDisplay(hours, minutes)}`}</Typography>
      <Typography
        variant='subtitle2'
        color='var(--mui-palette-text-secondary)'
        sx={{
          border: '1px solid var(--mui-palette-divider)',
          padding: '6px 12px',
          borderRadius: '4px',
        }}
      >
        {`Backups start within ${durationDisplay} of the scheduled times`}
      </Typography>
    </Stack>
  );
};
